/**
 * Priskluster (SEO/AEO) — Iter 4.
 *
 * Central, typad källa för alla /pris/<slug>-sidor som byggs på den delade
 * `priceGuideRoute`-helpern. Varje guide besvarar en pris-fråga med citerbart
 * kort svar (QAPage + Speakable), ett sakligt prisspann (tabell) och
 * kostnadsdrivare. Prisspann är generella branschspann ur Fenny offertdata —
 * inga bindande priser, ingen påhittad konkurrensdata.
 *
 * Kanoniska prissvar finns redan som flata URL:er (`/vad-kostar-en-foretagsfilm`,
 * `/vad-kostar-en-reklamfilm`, `/film-pris-kalkyl`). Priskluster bygger därför
 * ENDAST nya spann (filmproduktion, videoproduktion, employer branding,
 * abonnemang, produktfilm, sociala medier, animerad film) — ingen dubblering.
 */

export interface PriceTier {
  /** Kort etikett, t.ex. "Enklare produktion". */
  label: string;
  /** Prisspann, t.ex. "30–80 kkr". */
  range: string;
  /** Vad som ingår i nivån. */
  scope: string;
}

export interface PriceGuide {
  slug: string;
  /** Eyebrow ovanför H1. */
  eyebrow: string;
  /** Synlig H1. */
  title: string;
  /** Frågan som QAPage-schemat besvarar (citerbar). */
  question: string;
  /** <title> i head (≤60 tecken rekommenderat). */
  metaTitle: string;
  /** meta description (≤160 tecken). */
  metaDescription: string;
  /** Citerbart kort svar (1–3 meningar) — QuickAnswer + QAPage-svar. */
  shortAnswer: string;
  /** 1–2 stycken brödtext under kort svar. */
  intro: string[];
  /** Prisspann-tabell. */
  tiers: PriceTier[];
  /** Vad som driver priset. */
  drivers: string[];
  /** Så får ni mer film för budgeten. */
  savings: string[];
  /** Answer-library-ids → synlig FAQ + FAQPage JSON-LD. */
  faqIds: string[];
  /** Relaterad tjänstesida (CTA + brödtext). */
  relatedServicePath: string;
  relatedServiceLabel: string;
  /** Breadcrumb-etikett (kort). */
  breadcrumbLabel: string;
  /** Sitemap-prioritet. */
  priority: "high" | "normal";
}

export const PRICE_GUIDES: PriceGuide[] = [
  {
    slug: "filmproduktion",
    eyebrow: "Prisguide",
    title: "Pris för filmproduktion – jämför nivåer och upplägg",
    question: "Vad kostar filmproduktion i Sverige?",
    metaTitle: "Vad kostar filmproduktion? Prisspann 2026 | Fenny",
    metaDescription:
      "Vad kostar filmproduktion i Sverige? Spann 40 000–800 000 kr beroende på koncept, crew och dagar. Se kostnadsdrivare och när abonnemang sänker styckpriset.",
    shortAnswer:
      "Filmproduktion kostar typiskt 40 000–800 000 kr per produktion beroende på koncept, crew och antal produktionsdagar. Löpande filmproduktion via abonnemang börjar i stället från 14 995 kr/mån.",
    intro: [
      "Priset på filmproduktion avgörs av ambitionsnivån snarare än filmens längd. En enklare produktion med litet team och en inspelningsdag landar lågt i spannet; en producerad varumärkesfilm med flera dagar och fullt postflöde ligger högre.",
      "Det som driver kostnaden är hur många som krävs på set, hur mycket förproduktion konceptet behöver och hur omfattande efterarbetet blir i klipp, grade, ljud och eventuell VFX.",
    ],
    tiers: [
      { label: "Enklare produktion", range: "40–80 kkr", scope: "Litet team, 1 inspelningsdag, 1 kanalformat." },
      { label: "Producerad film", range: "120–250 kkr", scope: "Flera dagar, scenografi/regi, flera versioner." },
      { label: "Reklamfilm med koncept", range: "150–800 kkr+", scope: "Regissör, casting, flerdagsinspelning, VFX." },
      { label: "Löpande via Pulse", range: "från 14 995 kr/mån", scope: "Fast månadspris, jämn takt, allt ingår." },
    ],
    drivers: [
      "Antal personer på set (varje roll kostar tid och pengar).",
      "Hur mycket förproduktion och manus konceptet kräver.",
      "Efterarbete: klipp, grading, ljudmix, motion graphics och VFX.",
      "Antal inspelningsdagar och locations (recce, resa, logistik).",
    ],
    savings: [
      "Filma flera filmer samma dag — uppstart och rigg delas.",
      "Producera modulärt: en huvudfilm + flera kanalversioner.",
      "Gå från enskilda offerter till abonnemang vid återkommande behov.",
    ],
    faqIds: [
      "sv_vad_kostar_filmproduktion",
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
      "sv_vad_ingar_i_filmabonnemang",
      "sv_hur_lang_tid_tar_filmproduktion",
    ],
    relatedServicePath: "/filmproduktion",
    relatedServiceLabel: "Läs mer om filmproduktion",
    breadcrumbLabel: "Pris filmproduktion",
    priority: "high",
  },
  {
    slug: "videoproduktion",
    eyebrow: "Prisguide",
    title: "Videoproduktion: prisspann för projekt och abonnemang",
    question: "Vad kostar videoproduktion?",
    metaTitle: "Vad kostar videoproduktion? Prisspann 2026 | Fenny",
    metaDescription:
      "Vad kostar videoproduktion? Typiskt 30 000–250 000 kr per film beroende på omfattning — eller löpande från 14 995 kr/mån via abonnemang. Se vad som styr priset.",
    shortAnswer:
      "Videoproduktion kostar typiskt 30 000–250 000 kr per film beroende på omfattning. Med ett Pulse-abonnemang får ni i stället löpande videoproduktion till fast månadspris från 14 995 kr/mån.",
    intro: [
      "Videoproduktion är ett brett begrepp och priset styrs av samma faktorer oavsett filmtyp: antal inspelningsdagar, förproduktion och manus, om casting eller speaker behövs samt hur omfattande klipp, grading och versionering blir.",
      "Den stora skillnaden mot ett abonnemang är att projektpris debiteras per film, medan abonnemang byter offert mot fast månadskostnad där strategi, produktion och formatanpassning ingår.",
    ],
    tiers: [
      { label: "Enklare produktion", range: "30–80 kkr", scope: "1 inspelningsdag, 1 kanalformat." },
      { label: "Mer producerad film", range: "120–250 kkr", scope: "Flera dagar, scenografi, flera versioner." },
      { label: "Stort koncept", range: "250 kkr+", scope: "Reklamfilm med koncept och fullt produktionsvärde." },
      { label: "Löpande via Pulse", range: "från 14 995 kr/mån", scope: "Fast månadspris, löpande leverans." },
    ],
    drivers: [
      "Antal inspelningsdagar och storlek på team.",
      "Förproduktion, manus och eventuell casting/speaker.",
      "Klipp, grading och hur många versioner som krävs.",
      "Behov av animation, drönare, studio eller live.",
    ],
    savings: [
      "Samla flera ämnen till samma inspelningsdag.",
      "Återanvänd material i 6–12 månader i stället för att producera nytt varje kvartal.",
      "Vid löpande behov ger abonnemang lägre styckpris.",
    ],
    faqIds: [
      "sv_vad_kostar_videoproduktion",
      "sv_hur_fungerar_videoabonnemang",
      "sv_vad_ar_video_as_a_service",
      "sv_vad_ingar_i_filmabonnemang",
    ],
    relatedServicePath: "/videoproduktion",
    relatedServiceLabel: "Läs mer om videoproduktion",
    breadcrumbLabel: "Pris videoproduktion",
    priority: "high",
  },
  {
    slug: "employer-branding-film",
    eyebrow: "Prisguide",
    title: "Pris för employer branding-film – från porträtt till kampanj",
    question: "Vad kostar en employer branding-film?",
    metaTitle: "Vad kostar employer branding-film? | Fenny",
    metaDescription:
      "En employer branding-film kostar typiskt 40 000–150 000 kr beroende på omfattning. Vid löpande rekrytering jämnas kostnaden ut till ett fast månadspris.",
    shortAnswer:
      "En enskild employer branding-film kostar typiskt 40 000–150 000 kr beroende på omfattning. Löpande via Pulse jämnas kostnaden ut till ett fast månadspris.",
    intro: [
      "Priset styrs av samma faktorer som annan film: antal inspelningsdagar, antal medverkande och platser, manusarbete och hur påkostad efterbearbetningen är. En enkel medarbetarporträtt-serie ligger lågt i spannet, en genomarbetad rekryteringsfilm med flera platser och casting högre.",
      "För företag som rekryterar kontinuerligt blir styckkostnaden ofta missvisande — behovet är snarare ett jämnt flöde av kultur- och rekryteringsinnehåll. Då passar ett abonnemang bättre.",
    ],
    tiers: [
      { label: "Medarbetarporträtt", range: "40–70 kkr", scope: "1 dag, intervjuer, enklare klipp." },
      { label: "Rekryteringsfilm", range: "80–150 kkr", scope: "Flera platser, manus, casting, full versionering." },
      { label: "Flaggskeppsfilm", range: "150 kkr+", scope: "Karriärsida-film med regi och högt produktionsvärde." },
      { label: "Löpande via Pulse", range: "från 14 995 kr/mån", scope: "Jämn takt av kultur- och rekryteringsfilm." },
    ],
    drivers: [
      "Antal inspelningsdagar och platser.",
      "Antal medverkande och om casting behövs.",
      "Manusarbete och berättargrepp.",
      "Hur påkostad efterbearbetning och grafik blir.",
    ],
    savings: [
      "Kombinera en flaggskeppsfilm (Prime) med löpande klipp (Pulse).",
      "Filma flera medarbetare samma dag för en porträttserie.",
      "Versionera för karriärsida, LinkedIn och annonsformat ur samma material.",
    ],
    faqIds: [
      "sv_vad_kostar_employer_branding_film",
      "sv_vad_ar_employer_branding_film",
      "sv_vad_ingar_i_filmabonnemang",
    ],
    relatedServicePath: "/employer-branding-film",
    relatedServiceLabel: "Läs mer om employer branding-film",
    breadcrumbLabel: "Pris employer branding-film",
    priority: "high",
  },
  {
    slug: "filmabonnemang",
    eyebrow: "Prisguide",
    title: "Prisguide filmabonnemang — nivåer och vad som ingår",
    question: "Vad kostar ett filmabonnemang?",
    metaTitle: "Vad kostar ett filmabonnemang? | Fenny",
    metaDescription:
      "Ett filmabonnemang kostar en fast månadskostnad från 14 995 kr/mån där strategi, produktion och formatanpassning ingår. Se vad som styr nivån och vad ni får.",
    shortAnswer:
      "Ett filmabonnemang har en fast månadskostnad — Fenny Pulse börjar från 14 995 kr/mån — där strategi, manus, inspelning, klipp och formatanpassning ingår. Nivån styrs av hur mycket film ni behöver per månad.",
    intro: [
      "I stället för offert per film betalar ni en fast månadskostnad och får ett dedikerat team som lär känna varumärket över tid. Inga timdebiteringar och inga dolda kostnader.",
      "Nivån avgörs av produktionstakt — hur många filmer och versioner ni behöver per månad — inte av enskilda offerter. För team med löpande behov blir styckpriset per film klart lägre än vid projektpris.",
    ],
    tiers: [
      { label: "Ingångsnivå", range: "från 14 995 kr/mån", scope: "Löpande social- och kanalfilm i jämn takt." },
      { label: "Mellannivå", range: "skräddarsys", scope: "Högre takt, fler format och versioner per månad." },
      { label: "Full kapacitet", range: "skräddarsys", scope: "Flera kanaler, regelbundna produktionsdagar." },
      { label: "Projekt (Prime)", range: "offert per film", scope: "Enskilda större produktioner vid sidan av abonnemanget." },
    ],
    drivers: [
      "Produktionstakt — antal filmer och versioner per månad.",
      "Antal kanaler och format som ska fyllas.",
      "Behov av tillägg som casting, animation, drönare eller live.",
      "Hur mycket strategi och planering som ska ingå löpande.",
    ],
    savings: [
      "Räkna på er volym över ett år, inte per kampanj.",
      "Vid 8+ filmer/år är abonnemang nästan alltid billigast totalt.",
      "Kombinera abonnemang för flödet med projekt för lanseringarna.",
    ],
    faqIds: [
      "sv_vad_ingar_i_filmabonnemang",
      "sv_vad_ar_film_pa_abonnemang",
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
      "sv_hur_fungerar_videoabonnemang",
    ],
    relatedServicePath: "/filmabonnemang",
    relatedServiceLabel: "Allt om filmabonnemang",
    breadcrumbLabel: "Pris filmabonnemang",
    priority: "high",
  },
  {
    slug: "produktfilm",
    eyebrow: "Prisguide",
    title: "Prisguide produktfilm — spann och kostnadsdrivare",
    question: "Vad kostar en produktfilm?",
    metaTitle: "Vad kostar en produktfilm? Prisspann 2026 | Fenny",
    metaDescription:
      "En produktfilm kostar typiskt 25 000–150 000 kr beroende på studio, antal produkter och animation. Se kostnadsdrivare och hur ni får fler versioner per krona.",
    shortAnswer:
      "En produktfilm kostar typiskt 25 000–150 000 kr beroende på om den filmas i studio eller miljö, antal produkter och hur mycket animerad grafik som krävs. Löpande produktfilm via abonnemang sänker styckpriset.",
    intro: [
      "Produktfilm spänner från en enkel studio-loop för e-handel till en producerad lanseringsfilm med regi och motion graphics. Priset följer produktionsvärdet, inte filmens längd.",
      "Behöver ni många produkter filmade är det oftast effektivast att samla dem till samma inspelningsdag och versionera för flera kanaler.",
    ],
    tiers: [
      { label: "Studio-loop / e-handel", range: "25–50 kkr", scope: "1 produkt, ren bakgrund, korta klipp." },
      { label: "Produktfilm med story", range: "50–100 kkr", scope: "Miljö eller studio, flera klipp, grafik." },
      { label: "Lanseringsfilm", range: "100–150 kkr+", scope: "Regi, motion graphics, flera versioner." },
      { label: "Löpande via Pulse", range: "från 14 995 kr/mån", scope: "Återkommande produktfilm i jämn takt." },
    ],
    drivers: [
      "Studio kontra inspelning i miljö.",
      "Antal produkter och varianter som ska visas.",
      "Mängden animerad grafik (typiskt 10–40 kkr per minut).",
      "Antal kanalformat och versioner.",
    ],
    savings: [
      "Filma hela sortimentet samma dag för lägre styckkostnad.",
      "Klipp korta versioner för Reels, annons och produktsida ur samma material.",
      "Vid återkommande lanseringar ger abonnemang lägre pris per film.",
    ],
    faqIds: [
      "sv_vad_kostar_filmproduktion",
      "sv_vilka_filmformat_for_sociala_medier",
      "sv_vad_ingar_i_filmabonnemang",
    ],
    relatedServicePath: "/produktfilm",
    relatedServiceLabel: "Läs mer om produktfilm",
    breadcrumbLabel: "Pris produktfilm",
    priority: "normal",
  },
  {
    slug: "sociala-medier-film",
    eyebrow: "Prisguide",
    title: "Prisguide film för sociala medier — spann per format",
    question: "Vad kostar film för sociala medier?",
    metaTitle: "Vad kostar film för sociala medier? | Fenny",
    metaDescription:
      "Film för sociala medier kostar typiskt 15 000–60 000 kr per inspelningsdag som ger flera klipp. Löpande social film via abonnemang ger lägst pris per klipp.",
    shortAnswer:
      "Film för sociala medier kostar typiskt 15 000–60 000 kr per inspelningsdag, där en dag ofta ger flera korta klipp. Eftersom sociala kanaler behöver film löpande blir ett abonnemang vanligtvis billigast per klipp.",
    intro: [
      "Social film handlar om volym och takt snarare än enskilda dyra produktioner. En inspelningsdag kan ge ett tiotal vertikala klipp som versioneras för Reels, TikTok, LinkedIn och annons.",
      "Eftersom kanalerna behöver nytt innehåll varje vecka blir styckkostnad per film mindre relevant — det som räknas är hur mycket publicerbar film en månadsbudget ger.",
    ],
    tiers: [
      { label: "Halvdag content", range: "15–30 kkr", scope: "Några vertikala klipp från en plats." },
      { label: "Heldag content", range: "30–60 kkr", scope: "Ett tiotal klipp, flera ämnen, versionering." },
      { label: "Kampanjpaket", range: "60 kkr+", scope: "Koncept, annonsklipp och fler format." },
      { label: "Löpande via Pulse", range: "från 14 995 kr/mån", scope: "Jämnt flöde av social film varje månad." },
    ],
    drivers: [
      "Antal klipp och format per inspelningsdag.",
      "Hur mycket text, grafik och undertexter som krävs.",
      "Om koncept och manus behövs eller om det är löpande content.",
      "Publiceringstakt — fler kanaler kräver mer material.",
    ],
    savings: [
      "Producera batchvis: en dag ger material för flera veckor.",
      "Versionera ett klipp till 16:9, 9:16 och 1:1 i stället för att filma nytt.",
      "Abonnemang ger lägst pris per klipp vid hög publiceringstakt.",
    ],
    faqIds: [
      "sv_vilka_filmformat_for_sociala_medier",
      "sv_hur_ofta_bor_foretag_publicera_video",
      "sv_vad_kostar_videoproduktion",
    ],
    relatedServicePath: "/sociala-medier-film",
    relatedServiceLabel: "Läs mer om film för sociala medier",
    breadcrumbLabel: "Pris sociala medier-film",
    priority: "normal",
  },
  {
    slug: "animerad-film",
    eyebrow: "Prisguide",
    title: "Prisguide animerad film — spann och kostnadsdrivare",
    question: "Vad kostar en animerad film?",
    metaTitle: "Vad kostar en animerad film? Pris per minut 2026",
    metaDescription:
      "Motion graphics, 2D explainer eller 3D — se prisspann per minut och de fyra sakerna som avgör om er film hamnar i den lägre eller den högre delen av spannet.",

    shortAnswer:
      "Animerad film och motion graphics kostar typiskt 10 000–40 000 kr per färdig minut beroende på stil och komplexitet. Enkel 2D-grafik ligger lågt i spannet, avancerad 3D-animation högre.",
    intro: [
      "Animation prissätts per färdig minut eftersom varje sekund byggs för hand. Stilen avgör allt: enkel typografisk motion graphics går snabbt, medan karaktärsanimation och 3D kräver betydligt mer tid.",
      "En tydlig storyboard och ett färdigt manus innan produktionen startar håller nere kostnaden — ändringar sent i animationsfasen är dyra.",
    ],
    tiers: [
      { label: "Enkel motion graphics", range: "10–20 kkr/min", scope: "Typografi, ikoner, enkel 2D." },
      { label: "Avancerad 2D / explainer", range: "20–30 kkr/min", scope: "Illustration, karaktärer, speaker." },
      { label: "3D-animation", range: "30–40 kkr/min+", scope: "3D-modellering, rendering, komplex rörelse." },
      { label: "Löpande via Pulse", range: "från 14 995 kr/mån", scope: "Återkommande grafik och animerade klipp." },
    ],
    drivers: [
      "Stil: 2D motion graphics, illustration eller 3D.",
      "Längd — priset räknas per färdig minut.",
      "Mängden unik illustration eller 3D-modellering.",
      "Speaker, musik och ljuddesign.",
    ],
    savings: [
      "Lås storyboard och manus innan animationen börjar.",
      "Återanvänd grafikmallar för en serie filmer.",
      "Vid löpande grafikbehov ger abonnemang en jämnare kostnad.",
    ],
    faqIds: [
      "sv_vad_kostar_videoproduktion",
      "sv_vad_ingar_i_filmabonnemang",
      "sv_ar_film_pa_abonnemang_battre_an_projektpris",
    ],
    relatedServicePath: "/motion-graphics",
    relatedServiceLabel: "Läs mer om motion graphics",
    breadcrumbLabel: "Pris animerad film",
    priority: "normal",
  },
  {
    slug: "livsberattelse-film",
    eyebrow: "Prisguide",
    title: "Pris för filmad livsberättelse – från intervju till familjearv",
    question: "Vad kostar en filmad livsberättelse?",
    metaTitle: "Vad kostar en livsberättelse på film? | Fenny Heritage",
    metaDescription:
      "En filmad livsberättelse kostar typiskt 35 000–150 000 kr beroende på omfattning. Se prisspann, paket och vad som styr priset för en filmad familjeberättelse.",
    shortAnswer:
      "En filmad livsberättelse kostar typiskt 35 000–150 000 kr beroende på omfattning — från en enskild intervju till ett större familjearv med flera intervjuer, kapitelstruktur och inflätat arkivmaterial.",
    intro: [
      "Priset på en filmad livsberättelse styrs av hur många som intervjuas, hur lång inspelningen är, om foton och arkivmaterial ska vävas in och hur omfattande slutmaterialet ska vara — en huvudfilm, kapitel, korta utdrag eller en kombination.",
      "Vi ger en offert efter ett första samtal. Materialet levereras digitalt och stannar privat inom familjen om ni vill det.",
    ],
    tiers: [
      { label: "Livsintervjun", range: "35–55 kkr", scope: "En person, ett pre-möte, en filmad intervju och en klippt film." },
      { label: "Familjearvet", range: "65–95 kkr", scope: "Längre intervju, kapitelstruktur och inflätat fotomaterial." },
      { label: "Arvspaketet", range: "100–150 kkr+", scope: "Flera intervjuer, tydlig berättarstruktur, huvudfilm + korta klipp." },
      { label: "Vid akut hälsosituation", range: "offert", scope: "Vi prioriterar och kan komma igång med inspelning inom någon vecka." },
    ],
    drivers: [
      "Antal personer som intervjuas.",
      "Hur lång inspelningen är och om den sker över flera tillfällen.",
      "Mängd arkivmaterial (foton, dokument, äldre film) som ska bearbetas.",
      "Hur strukturerad slutleveransen ska vara — huvudfilm, kapitel eller båda.",
    ],
    savings: [
      "Börja med Livsintervjun — det är ofta nog för att ha berättelsen bevarad.",
      "Samla arkivmaterial innan vi börjar — det håller nere bearbetningstiden.",
      "Spela in flera familjemedlemmar samma dag om de finns på samma plats.",
    ],
    faqIds: [
      "sv_vad_kostar_livsberattelse_pa_film",
      "sv_hur_lang_tid_tar_livsberattelse",
      "sv_skillnad_livsberattelse_memoarbok",
      "sv_vad_ar_fenny_heritage",
    ],
    relatedServicePath: "/heritage/livsberattelser",
    relatedServiceLabel: "Läs mer om livsberättelser på film",
    breadcrumbLabel: "Pris livsberättelse på film",
    priority: "high",
  },
  {
    slug: "kunskapsoverforing",
    eyebrow: "Prisguide",
    title: "Prisguide kunskapsöverföring på film — spann och upplägg",
    question: "Vad kostar kunskapsöverföring på film?",
    metaTitle: "Vad kostar kunskapsöverföring på film? | Fenny Heritage",
    metaDescription:
      "Kunskapsöverföring på film kostar typiskt 60 000–250 000 kr beroende på antal nyckelpersoner och hur kapitlat materialet ska vara. Se prisspann och vad som styr priset.",
    shortAnswer:
      "Kunskapsöverföring på film kostar typiskt 60 000–250 000 kr beroende på antal nyckelpersoner, omfång och hur kapitlat materialet ska vara för onboarding och intern utbildning.",
    intro: [
      "Priset styrs av hur många nyckelpersoner som intervjuas, hur stor del av deras erfarenhet och metod som ska dokumenteras och hur färdigt materialet behöver vara för intern användning.",
      "För återkommande dokumentation — pensionsavgångar, generationsskiften, kritiska roller — bygger vi gärna en intervjuserie över tid i stället för en engångsinsats.",
    ],
    tiers: [
      { label: "Nyckelperson-intervjun", range: "60–95 kkr", scope: "En senior medarbetare, pre-möte, frågedesign, intervju och intern leverans." },
      { label: "Kunskapsöverföring", range: "100–180 kkr", scope: "Intervju eller intervjuserie, kapitlat material för onboarding och utbildning." },
      { label: "Företagsarvet", range: "180–250 kkr+", scope: "Flera personer, sammanfattande film och korta internklipp." },
      { label: "Löpande dokumentation", range: "offert", scope: "Återkommande intervjuer planerade över året, t.ex. vid pensionsavgångar." },
    ],
    drivers: [
      "Antal nyckelpersoner som ska intervjuas.",
      "Hur mycket förarbete som krävs för att kartlägga vad som ska bevaras.",
      "Antal kapitelklipp och versioner som ska klippas ur materialet.",
      "Om sekretess och säkerhetsrutiner kräver särskild hantering.",
    ],
    savings: [
      "Börja med en person och en kritisk roll — bygg ut när värdet är bevisat.",
      "Planera intervjuerna kring pensionsavgångar och rolltransitioner.",
      "Klipp en huvudfilm + korta kapitelklipp ur samma material i stället för flera inspelningar.",
    ],
    faqIds: [
      "sv_hur_gor_man_kunskapsoverforing_pa_film",
      "sv_vad_ar_fenny_heritage",
      "sv_vem_passar_heritage",
    ],
    relatedServicePath: "/heritage/kunskapsarv",
    relatedServiceLabel: "Läs mer om kunskapsarv på film",
    breadcrumbLabel: "Pris kunskapsöverföring",
    priority: "high",
  },
  {
    slug: "minnesfilm",
    eyebrow: "Prisguide",
    title: "Pris för minnesfilm – med eget material eller ny intervju",
    question: "Vad kostar en minnesfilm?",
    metaTitle: "Vad kostar en minnesfilm? Priser 2026 | Fenny Heritage",
    metaDescription:
      "En minnesfilm kostar från cirka 35 000 kr, och 45 000–75 000 kr med filmad intervju. Se prisspann, vad som styr priset och hur ni planerar inför visningen.",
    shortAnswer:
      "En minnesfilm kostar från cirka 35 000 kr när den byggs på insamlat material, och typiskt 45 000–75 000 kr om en filmad intervju med huvudpersonen ingår.",
    intro: [
      "Priset styrs av om vi spelar in nytt material eller bearbetar befintligt, hur mycket foto- och arkivmaterial som ska digitaliseras, och hur många hälsningar som ska samlas in och ljudsättas.",
      "Många kombinerar minnesfilm med en full livsberättelse — samma inspelning räcker till båda och blir betydligt billigare än två separata produktioner.",
    ],
    tiers: [
      { label: "Minnesfilm kort", range: "från 35 kkr", scope: "Foton och insamlat material klippt till en visningsfilm på 5–12 minuter." },
      { label: "Minnesfilm + intervju", range: "45–75 kkr", scope: "Filmad intervju med huvudpersonen plus minnesfilm ur samma material." },
      { label: "Familjearvet", range: "65–95 kkr", scope: "Full livsberättelse i kapitel med minnesfilm som kortversion." },
      { label: "Kort varsel", range: "offert", scope: "Vid minnesstund arbetar vi med kortare ledtid — hör av er direkt." },
    ],
    drivers: [
      "Om en ny intervju ska spelas in eller bara befintligt material bearbetas.",
      "Mängd foton, dokument och äldre film som ska digitaliseras.",
      "Antal hälsningar som ska samlas in, ljudsättas och färgsättas.",
      "Om filmen ska levereras till ett fast visningsdatum med kort varsel.",
    ],
    savings: [
      "Samla foton digitalt i förväg — det håller nere bearbetningstiden.",
      "Spela in hälsningar på distans med mobil enligt våra instruktioner.",
      "Gör minnesfilm och livsberättelse ur samma inspelning.",
      "Boka minst sex veckor före visningsdatumet när det går.",
    ],
    faqIds: [
      "sv_vad_kostar_minnesfilm",
      "sv_vad_ar_minnesfilm",
      "sv_vad_kostar_livsberattelse_pa_film",
      "sv_vad_ar_fenny_heritage",
    ],
    relatedServicePath: "/heritage/minnesfilm",
    relatedServiceLabel: "Läs mer om minnesfilm",
    breadcrumbLabel: "Pris minnesfilm",
    priority: "normal",
  },
];

const BY_SLUG = new Map(PRICE_GUIDES.map((g) => [g.slug, g]));

export function getPriceGuide(slug: string): PriceGuide {
  const g = BY_SLUG.get(slug);
  if (!g) throw new Error(`Unknown price guide: ${slug}`);
  return g;
}

export function getPriceGuideSlugs(): string[] {
  return PRICE_GUIDES.map((g) => g.slug);
}
